import { EroundMappingLine, EroundMappingLineRef, WallboxEntry } from "../types/onboarding";
import { parseCsv } from "./chargePointHistory";
import { modelKey } from "./modelKey";

// SOPS-169: the reviewed proposal written by `eround-automation catalogue-propose`
// (station-ops specs/SOPS-169-catalogue-eround-mapping/contracts/mapping-proposal.md).
// The reviewer decides each line: map, hide, or blank to leave it alone.

export interface ProposalLine {
  line: number;
  manufacturer: string;
  model: string;
  decision: "map" | "hide" | "";
  eroundManufacturer: string;
  eroundModel: string;
  chargingPoints: number | null;
}

export interface MappingImportPreview {
  send: EroundMappingLine[];
  counts: { map: number; hide: number; blank: number };
  notInCatalogue: EroundMappingLineRef[];
}

const NOT_A_PROPOSAL =
  "This is not a mapping proposal: it has no manufacturer, model and decision columns.";
const INCOMPLETE_MAP =
  "map needs the eRound manufacturer, model and charging points (at least 1).";

const toPoints = (value: string): number | null => {
  const points = Number(value.trim());
  return value.trim() && Number.isInteger(points) ? points : null;
};

export const parseMappingProposal = (
  text: string
): { lines: ProposalLine[]; problems: string[] } => {
  const [header = [], ...rows] = parseCsv(text.replace(/^\ufeff/, ""));
  const column = (name: string) => header.map((h) => h.trim().toLowerCase()).indexOf(name);
  const at = (row: string[], name: string) => (column(name) < 0 ? "" : row[column(name)] ?? "");
  if (["manufacturer", "model", "decision"].some((name) => column(name) < 0)) {
    return { lines: [], problems: [NOT_A_PROPOSAL] };
  }

  const lines: ProposalLine[] = [];
  const problems: string[] = [];
  rows.forEach((row, index) => {
    if (row.every((cell) => !cell.trim())) return;
    const line = index + 2;
    const decision = at(row, "decision").trim().toLowerCase();
    if (decision !== "map" && decision !== "hide" && decision !== "") {
      problems.push(`Line ${line}: "${at(row, "decision").trim()}" is not a decision — use map, hide or leave it blank.`);
      return;
    }
    const parsed: ProposalLine = {
      line,
      manufacturer: at(row, "manufacturer"),
      model: at(row, "model"),
      decision,
      eroundManufacturer: at(row, "eround_manufacturer").trim(),
      eroundModel: at(row, "eround_model").trim(),
      chargingPoints: toPoints(at(row, "charging_points")),
    };
    const complete =
      parsed.eroundManufacturer && parsed.eroundModel && (parsed.chargingPoints ?? 0) >= 1;
    if (decision === "map" && !complete) {
      problems.push(`Line ${line}: ${INCOMPLETE_MAP}`);
      return;
    }
    lines.push(parsed);
  });
  return { lines, problems };
};

const toRequestLine = ({ manufacturer, model, decision, ...mapping }: ProposalLine): EroundMappingLine =>
  decision === "map"
    ? { manufacturer, model, decision, eround_manufacturer: mapping.eroundManufacturer,
        eround_model: mapping.eroundModel, charging_points: mapping.chargingPoints ?? undefined }
    : { manufacturer, model, decision: "hide" };

// Lines are matched to entries the way station-ops and the server match models;
// a line that matches no entry would only come back as skipped, so it is not sent.
export const previewMappingImport = (
  lines: ProposalLine[],
  entries: WallboxEntry[]
): MappingImportPreview => {
  const known = new Set(entries.map((e) => modelKey(e.manufacturer, e.model_string)));
  const preview: MappingImportPreview = { send: [], counts: { map: 0, hide: 0, blank: 0 }, notInCatalogue: [] };
  lines.forEach((line) => {
    if (!line.decision) {
      preview.counts.blank += 1;
      return;
    }
    if (!known.has(modelKey(line.manufacturer, line.model))) {
      preview.notInCatalogue.push({ manufacturer: line.manufacturer, model: line.model });
      return;
    }
    preview.counts[line.decision] += 1;
    preview.send.push(toRequestLine(line));
  });
  return preview;
};


const csvCell = (value: string): string =>
  /[;"\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/** The catalogue in the `Marke;Modell` form `catalogue-propose` reads. */
export const catalogueCsv = (entries: WallboxEntry[]): string =>
  ["Marke;Modell", ...entries.map((e) => `${csvCell(e.manufacturer)};${csvCell(e.model_string)}`)].join("\n") + "\n";

export const downloadCatalogueCsv = (entries: WallboxEntry[]): void => {
  const blob = new Blob([catalogueCsv(entries)], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `wallbox-catalogue-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
